import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider, useUser } from './components/UserProvider';
import { LicenseProvider, useLicense } from './contexts/LicenseContext';
import { ThemeProvider } from './components/ThemeProvider';
import { MaterialDesign3Provider } from './components/MaterialDesign3Provider';
import { NotificationProvider } from './components/NotificationProvider';
import NotificationContainer from './components/NotificationContainer';
import { DeleteConfirmProvider } from './contexts/DeleteConfirmContext';
import { Suspense, lazy } from 'react';
import Layout from './layout/Layout';

// Core eagerly-loaded component
import VideoWall from './components/VideoWall';

import "react-checkbox-tree/lib/react-checkbox-tree.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import './App.css';

// Code-split pages on demand with React.lazy
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Cameras = lazy(() => import('./pages/Cameras'));
const CameraDetailPage = lazy(() => import('./pages/CameraDetailPage'));
const Playback = lazy(() => import('./pages/Playback'));
const LocalRecordingsPage = lazy(() => import('./pages/LocalRecordingsPage'));
const Detections = lazy(() => import('./pages/Detections'));
const Events = lazy(() => import('./pages/Events'));
const AiEventsPage = lazy(() => import('./pages/AiEventsPage'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Users = lazy(() => import('./pages/Users'));
const RolesPermissions = lazy(() => import('./pages/RolesPermissions'));
const SystemHealth = lazy(() => import('./pages/SystemHealth'));
const Logs = lazy(() => import('./pages/Logs'));
const Discovery = lazy(() => import('./pages/Discovery'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CameraMonitorPage = lazy(() => import('./pages/CameraMonitorPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const LocationGroups = lazy(() => import('./pages/LocationGroups'));
const LiveViewPage = lazy(() => import('./pages/LiveViewPage'));
const AIModules = lazy(() => import('./pages/AIModules'));
const FaceDetections = lazy(() => import('./pages/FaceDetections'));
const VehicleDetections = lazy(() => import('./pages/VehicleDetections'));
const LicensePlates = lazy(() => import('./pages/LicensePlates'));
const Blacklist = lazy(() => import('./pages/Blacklist'));
const BlacklistAlerts = lazy(() => import('./pages/BlacklistAlerts'));
const Snapshots = lazy(() => import('./pages/Snapshots'));
const KnownFaces = lazy(() => import('./pages/KnownFaces'));
const BlockedPersons = lazy(() => import('./pages/BlockedPersons'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const StudentManagement = lazy(() => import('./pages/StudentManagement'));
const AttendanceTracking = lazy(() => import('./pages/AttendanceTracking'));
const Subscription = lazy(() => import('./pages/Subscription'));
const About = lazy(() => import('./pages/About'));
const GISMapPage = lazy(() => import('./pages/GISMapPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LicensePage = lazy(() => import('./pages/LicensePage'));
const NotificationCenterPage = lazy(() => import('./pages/NotificationCenterPage'));
const IPSpeakersPage = lazy(() => import('./pages/IPSpeakersPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const PersonActivityIntelligencePage = lazy(() => import('./pages/PersonActivityIntelligencePage'));
const AiIntelligenceCenterPage = lazy(() => import('./pages/AiIntelligenceCenterPage'));
const SafetySecurityCompliancePage = lazy(() => import('./pages/SafetySecurityCompliancePage'));
const CampusIntelligencePage = lazy(() => import('./pages/CampusIntelligencePage'));
const NvidiaAiStudioPage = lazy(() => import('./pages/NvidiaAiStudioPage'));
const ServerMonitorPage = lazy(() => import('./pages/ServerMonitorPage'));

// High-performance smooth fallback loader for code-split routes
const PageLoader: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '12px' }}>
    <div style={{ width: '36px', height: '36px', border: '3px solid rgba(59, 130, 246, 0.2)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <span style={{ fontSize: '12px', fontWeight: 600, opacity: 0.6, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Loading Module...</span>
  </div>
);

// AuthGuard: redirect logged-in users away from /login and /register
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// PermRoute: require login + a specific permission to access a page.
// Unauthenticated → /login. Authenticated but no permission → /dashboard.
const PermRoute: React.FC<{ perm: string; children: React.ReactNode }> = ({ perm, children }) => {
  const { user, hasPermission } = useUser();
  if (!user) return <Navigate to="/login" replace />;
  if (perm === 'ai_events:view') {
    const canEvents = hasPermission('ai_events:view') ||
      hasPermission('ai_events:table_view') ||
      hasPermission('ai_events:timeline_view') ||
      hasPermission('ai_modules:view');
    if (!canEvents) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
  }
  if (perm === 'ai_modules:view') {
    const canModules = hasPermission('ai_modules:view') ||
      hasPermission('ai_modules:tab_modules') ||
      hasPermission('ai_modules:tab_features') ||
      hasPermission('ai_modules:tab_licensing') ||
      hasPermission('ai_modules:tab_pricing') ||
      hasPermission('ai_modules:tab_studio') ||
      hasPermission('ai_modules:tab_services') ||
      hasPermission('ai_modules:manage');
    if (!canModules) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
  }
  if (!hasPermission(perm)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// LicFeatureRoute: require a licensed feature in addition to login.
// Unlicensed feature → redirect to /dashboard with no error (menu is hidden anyway).
const LicFeatureRoute: React.FC<{ feature: string; children: React.ReactNode }> = ({ feature, children }) => {
  const { user } = useUser();
  const { hasFeature, loaded } = useLicense();
  if (!user) return <Navigate to="/login" replace />;
  if (!loaded) return <PageLoader />;
  if (!hasFeature(feature)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <MaterialDesign3Provider>
        <UserProvider>
          <LicenseProvider>
          <NotificationProvider>
          <DeleteConfirmProvider>
            <Router>
              <div className="App">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Authentication routes */}
                    <Route path="/login" element={
                      <AuthGuard>
                        <LoginPage />
                      </AuthGuard>
                    } />
                    <Route path="/register" element={
                      <AuthGuard>
                        <RegisterPage />
                      </AuthGuard>
                    } />

                    {/* Main application routes with layout */}
                    <Route path="/" element={<Layout><Dashboard /></Layout>} />
                    <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
                    <Route path="/video-wall" element={<LicFeatureRoute feature="live_view"><Layout><VideoWall /></Layout></LicFeatureRoute>} />

                    <Route path="/cameras" element={<LicFeatureRoute feature="cameras"><PermRoute perm="cameras:view"><Layout><Cameras /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/cameras/:id" element={<LicFeatureRoute feature="cameras"><PermRoute perm="cameras:view"><Layout><CameraDetailPage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/live-view" element={<LicFeatureRoute feature="live_view"><PermRoute perm="live_view:view"><LiveViewPage /></PermRoute></LicFeatureRoute>} />
                    <Route path="/playback" element={<LicFeatureRoute feature="playback"><PermRoute perm="playback:view"><Layout><Playback /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/recordings" element={<PermRoute perm="cameras:view"><Layout><LocalRecordingsPage /></Layout></PermRoute>} />
                    <Route path="/local-recordings" element={<PermRoute perm="cameras:view"><Layout><LocalRecordingsPage /></Layout></PermRoute>} />
                    <Route path="/camera-monitor" element={<LicFeatureRoute feature="camera_monitor"><PermRoute perm="camera_monitor:view"><Layout><CameraMonitorPage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/gis" element={<LicFeatureRoute feature="gis_floor_map"><PermRoute perm="gis:view"><Layout><GISMapPage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/users" element={<PermRoute perm="users:view"><Layout><Users /></Layout></PermRoute>} />
                    <Route path="/roles" element={<PermRoute perm="roles:view"><Layout><RolesPermissions /></Layout></PermRoute>} />
                    <Route path="/discovery" element={<LicFeatureRoute feature="cameras"><PermRoute perm="cameras:create"><Layout><Discovery /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/location-groups" element={<LicFeatureRoute feature="cameras"><PermRoute perm="location_groups:manage"><Layout><LocationGroups /></Layout></PermRoute></LicFeatureRoute>} />

                    <Route path="/ai-modules" element={<LicFeatureRoute feature="analytics"><PermRoute perm="ai_modules:view"><Layout><AIModules /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/detections" element={<LicFeatureRoute feature="analytics"><Layout><Detections /></Layout></LicFeatureRoute>} />
                    <Route path="/events" element={<LicFeatureRoute feature="analytics"><Layout><Events /></Layout></LicFeatureRoute>} />
                    <Route path="/ai-events" element={<LicFeatureRoute feature="analytics"><PermRoute perm="ai_events:view"><Layout><AiEventsPage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/ai-intelligence-center" element={<LicFeatureRoute feature="analytics"><PermRoute perm="ai_modules:view"><Layout><AiIntelligenceCenterPage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/safety-security" element={<LicFeatureRoute feature="analytics"><PermRoute perm="ai_modules:view"><Layout><SafetySecurityCompliancePage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/campus-intelligence" element={<LicFeatureRoute feature="analytics"><PermRoute perm="ai_modules:view"><Layout><CampusIntelligencePage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/schools-colleges" element={<LicFeatureRoute feature="analytics"><PermRoute perm="ai_modules:view"><Layout><CampusIntelligencePage /></Layout></PermRoute></LicFeatureRoute>} />
                    <Route path="/nvidia-ai" element={<Layout><NvidiaAiStudioPage /></Layout>} />
                    <Route path="/analytics" element={<LicFeatureRoute feature="analytics"><Layout><Analytics /></Layout></LicFeatureRoute>} />
                    <Route path="/system-health" element={<Layout><SystemHealth /></Layout>} />
                    <Route path="/logs" element={<Layout><Logs /></Layout>} />
                    <Route path="/face-detections" element={<LicFeatureRoute feature="face_recognition"><Layout><FaceDetections /></Layout></LicFeatureRoute>} />
                    <Route path="/vehicle-detections" element={<LicFeatureRoute feature="analytics"><Layout><VehicleDetections /></Layout></LicFeatureRoute>} />
                    <Route path="/license-plates" element={<LicFeatureRoute feature="analytics"><Layout><LicensePlates /></Layout></LicFeatureRoute>} />
                    <Route path="/blacklist" element={<LicFeatureRoute feature="face_recognition"><Layout><Blacklist /></Layout></LicFeatureRoute>} />
                    <Route path="/blacklist-alerts" element={<LicFeatureRoute feature="face_recognition"><Layout><BlacklistAlerts /></Layout></LicFeatureRoute>} />
                    <Route path="/known-faces" element={<LicFeatureRoute feature="face_recognition"><Layout><KnownFaces /></Layout></LicFeatureRoute>} />
                    <Route path="/blocked-persons" element={<LicFeatureRoute feature="face_recognition"><Layout><BlockedPersons /></Layout></LicFeatureRoute>} />
                    <Route path="/snapshots" element={<LicFeatureRoute feature="cameras"><Layout><Snapshots /></Layout></LicFeatureRoute>} />
                    <Route path="/search" element={<LicFeatureRoute feature="cameras"><Layout><SearchPage /></Layout></LicFeatureRoute>} />
                    <Route path="/students" element={<LicFeatureRoute feature="face_recognition"><Layout><StudentManagement /></Layout></LicFeatureRoute>} />
                    <Route path="/attendance" element={<LicFeatureRoute feature="face_recognition"><Layout><AttendanceTracking /></Layout></LicFeatureRoute>} />
                    <Route path="/subscription" element={<Layout><Subscription /></Layout>} />
                    <Route path="/about" element={<Layout><About /></Layout>} />
                    <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
                    <Route path="/license" element={<Layout><LicensePage /></Layout>} />
                    <Route path="/notification-center" element={<LicFeatureRoute feature="notifications"><Layout><NotificationCenterPage /></Layout></LicFeatureRoute>} />
                    <Route path="/speakers" element={<LicFeatureRoute feature="ip_speakers"><Layout><IPSpeakersPage /></Layout></LicFeatureRoute>} />
                    <Route path="/announcements" element={<LicFeatureRoute feature="voice_announcements"><Layout><AnnouncementsPage /></Layout></LicFeatureRoute>} />
                    <Route path="/activity-intelligence" element={<LicFeatureRoute feature="face_recognition"><Layout><PersonActivityIntelligencePage /></Layout></LicFeatureRoute>} />
                    <Route path="/server-management" element={<Layout><ServerMonitorPage /></Layout>} />
                    <Route path="/server-monitor" element={<Layout><ServerMonitorPage /></Layout>} />

                  </Routes>
                </Suspense>
                <NotificationContainer />
              </div>
            </Router>
          </DeleteConfirmProvider>
          </NotificationProvider>
          </LicenseProvider>
        </UserProvider>
      </MaterialDesign3Provider>
    </ThemeProvider>
  );
}

export default App;
